/**
 * API Client for REST endpoints
 * Handles authentication, error handling, and request/response formatting
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '../config/env';
import type {
  User,
  Model,
  Conversation,
  ConversationListItem,
  ApiError,
  SessionInfo,
  ContextWindowStatus,
  Folder,
  UploadedFile,
  FileQuota,
} from '../types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiUrl,
      timeout: 30000,
      withCredentials: true, // Include cookies for session management
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Add request ID for tracing
        config.headers['X-Request-ID'] = this.generateRequestId();
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        // Do NOT hard-redirect on 401 here - let AuthContext and
        // ProtectedRoute handle authentication flow via React Router.
        // The previous window.location.href = '/login' was causing
        // a blank page by racing with React's rendering.

        // Format error for consistent handling
        const apiError: ApiError = error.response?.data || {
          error: {
            code: 'NETWORK_ERROR',
            message: error.message || 'Network error occurred',
            retryable: true,
          },
          requestId: error.config?.headers?.['X-Request-ID'] as string || '',
          timestamp: new Date().toISOString(),
        };

        return Promise.reject(apiError);
      }
    );
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Generic request methods
  async get<T = any>(url: string, config?: Parameters<AxiosInstance['get']>[1]) {
    return this.client.get<T>(url, config);
  }

  // Authentication endpoints
  async getSession(): Promise<SessionInfo> {
    const response = await this.client.get('/auth/session');
    // Backend returns { valid: true, user: {...} }
    return {
      user: response.data.user,
      expiresAt: response.data.expiresAt || new Date(Date.now() + 86400000).toISOString(),
    };
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
  }

  async acceptTerms(): Promise<void> {
    await this.client.post('/auth/accept-terms');
  }

  // User endpoints
  async getUserProfile(): Promise<User> {
    const response = await this.client.get('/api/user/profile');
    return response.data.profile || response.data;
  }

  async updateUserProfile(updates: { name?: string; institution?: string }): Promise<User> {
    const response = await this.client.patch('/api/user/profile', updates);
    return response.data.profile || response.data;
  }

  async updateUserPreferences(preferences: { theme?: string; defaultModel?: string; autoContextManagement?: boolean }): Promise<void> {
    await this.client.patch('/api/user/preferences', preferences);
  }

  async exportUserData(): Promise<Blob> {
    const response = await this.client.get('/api/user/export', {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteUserData(): Promise<void> {
    await this.client.delete('/api/user/data');
  }

  // Model endpoints
  async getModels(): Promise<Model[]> {
    const response = await this.client.get('/api/models');
    // Backend wraps in { models: [...] }
    return response.data.models || response.data;
  }

  async getModel(id: string): Promise<Model> {
    const response = await this.client.get(`/api/models/${id}`);
    return response.data.model || response.data;
  }

  // Conversation endpoints
  async getConversations(options?: {
    limit?: number;
    offset?: number;
    sortBy?: 'lastUpdated' | 'created';
    folderId?: string;
  }): Promise<ConversationListItem[]> {
    const response = await this.client.get('/api/conversations', {
      params: options,
    });
    // Backend wraps in { conversations: [...] }
    return response.data.conversations || response.data;
  }

  async createConversation(modelId?: string): Promise<Conversation> {
    const response = await this.client.post('/api/conversations', modelId ? { modelId } : {});
    return response.data.conversation || response.data;
  }

  async getConversation(id: string): Promise<Conversation> {
    const response = await this.client.get(`/api/conversations/${id}`);
    return response.data.conversation || response.data;
  }

  async deleteConversation(id: string): Promise<void> {
    await this.client.delete(`/api/conversations/${id}`);
  }

  async searchConversations(query: string): Promise<ConversationListItem[]> {
    const response = await this.client.get('/api/conversations/search', {
      params: { q: query },
    });
    return response.data.conversations || response.data;
  }

  async createSubchat(
    parentConversationId: string,
    messageId: string,
    excerpt: string,
    modelId?: string
  ): Promise<Conversation> {
    const response = await this.client.post(`/api/conversations/${parentConversationId}/subchats`, {
      messageId,
      excerpt,
      ...(modelId && { modelId }),
    });
    return response.data.conversation || response.data;
  }

  async getSubchats(parentConversationId: string): Promise<Conversation[]> {
    const response = await this.client.get(`/api/conversations/${parentConversationId}/subchats`);
    return response.data.conversations || response.data;
  }

  async getContextWindowStatus(conversationId: string, contextWindowSize: number): Promise<ContextWindowStatus> {
    const response = await this.client.get<ContextWindowStatus>(
      `/api/conversations/${conversationId}/context-status`,
      {
        params: { contextWindowSize },
      }
    );
    return response.data;
  }

  async updateConversationMetadata(
    conversationId: string,
    updates: { title?: string; folderId?: string | null; currentModelId?: string }
  ): Promise<void> {
    await this.client.patch(`/api/conversations/${conversationId}`, updates);
  }

  // Folder endpoints
  async getFolders(): Promise<Folder[]> {
    const response = await this.client.get<Folder[]>('/api/folders');
    return response.data;
  }

  async createFolder(name: string): Promise<Folder> {
    const response = await this.client.post<Folder>('/api/folders', { name });
    return response.data;
  }

  async updateFolder(id: string, name: string): Promise<void> {
    await this.client.patch(`/api/folders/${id}`, { name });
  }

  async deleteFolder(id: string): Promise<void> {
    await this.client.delete(`/api/folders/${id}`);
  }

  // File library endpoints
  async uploadFile(file: File): Promise<UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/api/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.file;
  }

  async listFiles(): Promise<{ files: UploadedFile[]; quota: FileQuota }> {
    const response = await this.client.get('/api/files');
    return response.data;
  }

  async deleteFile(fileId: string): Promise<void> {
    await this.client.delete(`/api/files/${fileId}`);
  }

  // Health check
  async healthCheck(): Promise<{ status: string; services: Record<string, boolean> }> {
    const response = await this.client.get('/health');
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
